import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  APP_INITIALIZER,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, HttpClient, withXhr } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { provideFunctions, getFunctions, connectFunctionsEmulator } from '@angular/fire/functions';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideAppCheck, initializeAppCheck, ReCaptchaEnterpriseProvider } from '@angular/fire/app-check';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { I18nService } from './core/services/i18n.service';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslateLoader, provideTranslateService, provideTranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

/**
 * Application Configuration
 * Configures Angular providers including:
 * - Routing
 * - Animations (Angular Material)
 * - Firebase (Auth, Firestore)
 * - HTTP Client
 */
const APP_CHECK_SITE_KEY: string = (environment as { appCheck?: { siteKey?: string } }).appCheck?.siteKey ?? '';

// reCAPTCHA Enterprise rejects localhost (not in the key's allowed domains), so local
// dev uses an App Check debug token. The SDK prints it in the console the first time;
// register it in Firebase console → App Check → Apps → Manage debug tokens.
// Set it explicitly (false off-localhost): AngularFire's provideAppCheck does
// `FIREBASE_APPCHECK_DEBUG_TOKEN ??= true` whenever isDevMode(), which would make
// development builds deployed to Hosting use the debug provider instead of reCAPTCHA.
if (APP_CHECK_SITE_KEY) {
  (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
    ['localhost', '127.0.0.1'].includes(location.hostname);
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', floatLabel: 'always' } },
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withXhr()),

    // ngx-translate root providers (v18: provider functions instead of TranslateModule)
    provideTranslateService({
      loader: provideTranslateLoader(() => HttpLoaderFactory(inject(HttpClient), inject(DOCUMENT))),
    }),

    // i18n Initializer - Wait for translations to load
    {
      provide: APP_INITIALIZER,
      useFactory: (i18nService: I18nService) => {
        return () => {
          return firstValueFrom(
            i18nService
              .getTranslations$()
              .pipe(filter((translations) => Object.keys(translations).length > 0)),
          );
        };
      },
      deps: [I18nService],
      multi: true,
    },

    // Firebase Configuration
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideFunctions(() => getFunctions(undefined, 'us-central1')),
    provideStorage(() => getStorage()),

    // App Check: only when a site key is configured (environment.appCheck.siteKey).
    // Required before enforcing App Check on Cloud Functions / Firestore / Storage.
    ...(APP_CHECK_SITE_KEY
      ? [
          provideAppCheck(() =>
            initializeAppCheck(undefined, {
              provider: new ReCaptchaEnterpriseProvider(APP_CHECK_SITE_KEY),
              isTokenAutoRefreshEnabled: true,
            }),
          ),
        ]
      : []),
  ],
};

// Factory for loading translation files from assets/i18n
export function HttpLoaderFactory(http: HttpClient, doc: Document): TranslateLoader {
  return {
    getTranslation: (lang: string) => {
      const baseHref = doc.querySelector('base')?.getAttribute('href') || '/';
      const origin = doc.location.origin;
      const assetsUrl = new URL(baseHref, origin).href.replace(/\/$/, '');
      return http.get<Record<string, any>>(`${assetsUrl}/assets/i18n/${lang}.json`);
    },
  };
}
